import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
  MenuItem,
  Select,
} from "@mui/material";
import apiClient from "../api/apiClient";
import { useSnackbar } from "../context/SnackbarProvider";

const EventModal = ({
  open,
  onClose,
  event,
  fetchEvents,
  useSuperAdmin = false,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    date: "",
    venue: "",
    description: "",
    logo: null,
    capacity: "",
    eventType: "public",
    employeeData: null,
    tableImages: [],
  });

  const [loading, setLoading] = useState(false);
  const showSnackbar = useSnackbar();

  useEffect(() => {
    if (event) {
      setFormData({
        name: event.name || "",
        slug: event.slug || "",
        date: event.date
          ? new Date(event.date).toISOString().split("T")[0]
          : "",
        venue: event.venue || "",
        description: event.description || "",
        logo: null,
        capacity: event.capacity?.toString() || "",
        eventType: event.eventType || "public",
        employeeData: null,
        tableImages: [],
      });
    } else {
      setFormData({
        name: "",
        slug: "",
        date: "",
        venue: "",
        description: "",
        logo: null,
        capacity: "",
        eventType: "public",
        employeeData: null,
        tableImages: [],
      });
    }
  }, [event]);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? (name === "tableImages" ? [...files] : files[0]) : value,
    }));
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData((prev) => ({
      ...prev,
      name,
      slug: generateSlug(name),
    }));
  };

  const handleTemplateDownload = async () => {
    try {
      const response = await apiClient.get("/events/download-template", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "employee_template.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      showSnackbar("Failed to download template.", "error");
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.date || !formData.venue) {
      showSnackbar("Please fill all the required fields.", "error");
      return;
    }

    if (
      formData.capacity &&
      (isNaN(Number(formData.capacity)) || Number(formData.capacity) <= 0)
    ) {
      showSnackbar("Capacity must be a positive number.", "error");
      return;
    }

    setLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name);
      formDataToSend.append(
        "slug",
        formData.slug || generateSlug(formData.name)
      );
      formDataToSend.append("date", formData.date);
      formDataToSend.append("venue", formData.venue);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("capacity", formData.capacity || "999");
      formDataToSend.append("eventType", formData.eventType);

      if (formData.logo) {
        formDataToSend.append("logo", formData.logo);
      }

      if (formData.eventType === "employee" && formData.employeeData) {
        formDataToSend.append("employeeData", formData.employeeData);
      }

      if (
        formData.eventType === "employee" &&
        formData.tableImages.length > 0
      ) {
        Array.from(formData.tableImages).forEach((file) => {
          formDataToSend.append("tableImages", file);
        });
      }
      const endpointBase = useSuperAdmin ? "/superadmin/events" : "/events";
      if (event) {
        await apiClient.put(`${endpointBase}/${event._id}`, formDataToSend);
        showSnackbar("Event updated successfully!", "success");
      } else {
        await apiClient.post("/events", formDataToSend);
        showSnackbar("Event created successfully!", "success");
      }

      fetchEvents();
      onClose();
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to save event.";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          bgcolor: "primary.main",
          color: "white",
          display: "flex",
          justifyContent: event ? "center" : "space-between", // Center when editing
          alignItems: "center",
        }}
      >
        {event ? "Edit Event" : "Create Event"}
      </DialogTitle>
      <DialogContent>
        {!event && (
          <TextField
            select
            fullWidth
            name="eventType"
            label="Event Type*"
            value={formData.eventType}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                eventType: e.target.value,
              }))
            }
            size="medium"
            sx={{ mt: 1.5 }}
          >
            <MenuItem value="public">Public Event</MenuItem>
            <MenuItem value="employee">Employee Event</MenuItem>
          </TextField>
        )}
        <TextField
          fullWidth
          name="name"
          label="Event Name*"
          value={formData.name}
          onChange={handleNameChange}
          sx={{ my: 2 }}
        />
        <TextField
          fullWidth
          name="slug"
          label="Slug"
          value={formData.slug}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          name="date"
          label="Date*"
          type="date"
          value={formData.date}
          onChange={handleInputChange}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          name="venue"
          label="Venue*"
          value={formData.venue}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          name="description"
          label="Description"
          multiline
          rows={3}
          value={formData.description}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          name="capacity"
          label="Capacity"
          type="number"
          value={formData.capacity}
          onChange={handleInputChange}
          sx={{ mb: 2 }}
        />
        <Typography variant="subtitle1" sx={{ color: "primary.main" }}>
          Upload Event Logo (Image File)
        </Typography>
        <TextField
          fullWidth
          name="logo"
          type="file"
          onChange={handleInputChange}
          inputProps={{ accept: "image/*" }}
          sx={{ mb: 2 }}
        />

        {formData.eventType === "employee" && (
          <>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleTemplateDownload}
              sx={{ mb: 2 }}
            >
              Download Employee Data Template
            </Button>
            <Box>
              <Typography variant="subtitle1" sx={{ color: "primary.main" }}>
                Upload Employee Data (CSV File)
              </Typography>
              <Typography variant="body2" sx={{ color: "#777", mb: 1 }}>
                Upload a CSV file containing Employee IDs, Table Numbers, and
                Employee Names
              </Typography>
              <TextField
                fullWidth
                name="employeeData"
                type="file"
                onChange={handleInputChange}
                inputProps={{ accept: ".csv,.xlsx,.xls" }}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ color: "primary.main" }}>
                Upload Table Images
              </Typography>
              <Typography variant="body2" sx={{ color: "#777", mb: 1 }}>
                Select multiple table images (one for each table)
              </Typography>
              <TextField
                fullWidth
                name="tableImages"
                type="file"
                inputProps={{ multiple: true, accept: "image/*" }}
                onChange={handleInputChange}
                sx={{ mb: 2 }}
              />
            </Box>
            {formData.tableImages.length > 0 && (
              <List>
                {Array.from(formData.tableImages).map((file, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={file.name} />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : event ? (
            "Save Changes"
          ) : (
            "Create Event"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventModal;
